import React, { useState, useEffect } from 'react';
import { Form, Select, Input, InputNumber, message, Table, Spin, Button } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import type { ColumnsType } from 'antd/es/table';
import { useParams, useNavigate } from 'react-router-dom';

const { Option } = Select;

interface PadronData {
  id: number;
  razon: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

interface OrdenData {
  id: number;
  orden: string;
  id_insumo: number;
  insumo_descripcion: string;
  tipo_movimiento: number;
  cantidad: number;
  cantidad_facturada: number;
  tasaiva: number;
  precio_unitario: number;
  subtotal: number;
  iva: number;
  iinterno: number;
  id_empresa: number;
  id_sucursal: number;
}

interface NuevoGridData {
  id: number;
  insumo: string;
  fecha_entrega?: string;
  id_insumo: number;
  cantidad_ingresada: number;
  orden: string;
  cantidad_facturada: number;
  precio_unitario: number;
  impuesto_interno: number;
  subtotal: number;
  tasa_iva: number;
  iva: number;
  total: number;
}

interface Percepcion {
  id: number;
  provincia: string;
  sigla: string;
  alta: number;
}

// Agregar nueva interface para el grid de percepciones
interface PercepcionGridData {
  id: number;
  percepcion: string;
  importe: number;
}


// Nueva interfaz para agrupar todos los datos
interface ComprobanteCompleto {
  comprobantes: {
    tipo: string;
    nro_comprobante: string;
    padron_id: number;
    padron_nombre: string;
    fecha_emision: string;
    id_empresa: number;
    id_sucursal: number;
  };
  movimientos: Array<{
    id: number;
    id_insumo: number;
    insumo: string;
    cantidad: number;
    cantidad_facturada: number;
    tasa_iva: number;
    precio_unitario: number;
    subtotal: number;
    iva: number;
    iinterno: number;
    tipo_movimiento: number;
    ordencompra: string;
    id_empresa: number;
    id_sucursal: number;
  }>;
  percepciones: Array<{
    id: number;
    percepcion: string;
    importe: number;
    id_empresa: number;
    id_sucursal: number;
  }>;
}

const EditarIngresos: React.FC = () => {
  const [form] = Form.useForm();
  const { getAccessTokenSilently, user, isLoading: isLoadingAuth } = useAuth0();
  const [padrones, setPadrones] = useState<PadronData[]>([]);
  const [ordenes, setOrdenes] = useState<OrdenData[]>([]);
  const [isLoadingOrdenes, setIsLoadingOrdenes] = useState<boolean>(false);
  const empresa = user?.id_empresa;
  const sucursal = user?.id_sucursal;
  const [puntoVenta, setPuntoVenta] = useState<string>('');
  const [numeroComprobante, setNumeroComprobante] = useState<string>('');
  const [impuestoInterno, setImpuestoInterno] = useState<number>(0);
  const [subtotalSinIva, setSubtotalSinIva] = useState<number>(0);
  const [impuestoIva, setImpuestoIva] = useState<number>(0);
  const [subLinea, setSubLinea] = useState<number>(0);
  const [ordenCompra, setOrdenCompra] = useState<string>('');
  const [descripcionInsumo, setDescripcionInsumo] = useState<string>('');
  const [cantidadIngresada, setCantidadIngresada] = useState<number>(0);
  const [cantidadFacturada, setCantidadFacturada] = useState<number>(0);
  const [precioUnitario, setPrecioUnitario] = useState<number>(0);
  const [isLoadingPadron, setIsLoadingPadron] = useState<boolean>(true);
  const [selectedRow, setSelectedRow] = useState<number | null>(null);
  const [selectedTasaIva, setSelectedTasaIva] = useState<string>('0');
  const [datosNuevo, setDatosNuevo] = useState<NuevoGridData[]>([]);
  const [totalImpuestoInterno, setTotalImpuestoInterno] = useState<number>(0);
  const [totalSubtotalSinIva, setTotalSubtotalSinIva] = useState<number>(0);
  const [totalIva, setTotalIva] = useState<number>(0);
  const [totalGeneral, setTotalGeneral] = useState<number>(0);
  const [totalPercepciones, setTotalPercepciones] = useState<number>(0);
  const [percepciones, setPercepciones] = useState<Percepcion[]>([]);
  const [selectedPercepcion, setSelectedPercepcion] = useState<string>('');
  const [loadingPercepciones, setLoadingPercepciones] = useState<boolean>(false);
  const [valorPercepcion, setValorPercepcion] = useState<number>(0);
  const [percepcionesGrid, setPercepcionesGrid] = useState<PercepcionGridData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<string>('');
  const [insumos, setInsumos] = useState<Array<{ id: number, descripcion: string, tasaiva: number }>>([]);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const { id } = useParams<{ id: string }>();
  const [comprobanteData, setComprobanteData] = useState<ComprobanteCompleto | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [idInsumo, setIdInsumo] = useState<number>(0);
  const navigate = useNavigate();

  // Función para cargar los padrones
  const fetchPadrones = async () => {
    setIsLoadingPadron(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_padron/${empresa}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Error al cargar padrones');
      }

      const data = await response.json();
      setPadrones(data);
      console.log('padrones:', data);
    } catch (error) {
      console.error('Error cargando padrones:', error);
      message.error('Error al cargar los padrones');
    } finally {
      setIsLoadingPadron(false);
    }
  };

  // Función para cargar las órdenes del proveedor
  const fetchOrdenes = async (proveedorId: number) => {
    setIsLoadingOrdenes(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      console.log('proveedorId:', empresa, sucursal, proveedorId);
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/pendiente_ocompra_dos/${empresa}/${sucursal}/${proveedorId}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Error al cargar órdenes');
      }

      const data = await response.json();
      console.log('xaqui paso', data);
      console.log('Datos recibidos:', JSON.stringify(data, null, 2));
      setOrdenes(data);
      //    console.log('xaqui_orden_compra',data,ordenes);
    } catch (error) {
      console.error('Error cargando órdenes:', error);
      message.error('Error al cargar las órdenes del proveedor');
    } finally {
      setIsLoadingOrdenes(false);
    }
  };

  // Manejador del cambio de proveedor
  const handleProveedorChange = (value: number) => {
    const padron = padrones.find(p => p.id === value);
    if (padron) {
      fetchOrdenes(padron.id);
    }
  };

  useEffect(() => {
    if (isLoadingData) {
      // Mostrar mensaje de carga
      const loadingMessage = message.loading({
        content: 'Cargando datos...',
        duration: 0,
        key: 'loadingData'
      });

      // Cleanup function
      return () => {
        loadingMessage(); // Cerrar el mensaje de carga
      };
    }
  }, [isLoadingData]);

  // Cargar padrones cuando el componente se monta
  useEffect(() => {
    if (!isLoadingAuth && empresa) {
      const initializeData = async () => {
        setIsLoadingData(true);
        await fetchPadrones();
        if (id) {
          await fetchComprobanteData();
          setIsLoadingData(false);
        }
      };
      initializeData();
    }
  }, [isLoadingAuth, empresa, id]);

  //const onFinish = (values: any) => {
  //  console.log('Valores del formulario:', values);
  //};

  // Manejador para el botón Ingresar
  const handleIngresar = (record: OrdenData) => {
    try {
      if (!record) {
        console.error('No se recibió registro para procesar');
        return;
      }

      console.log('Datos recibidos en handleIngresar:', record); // Debug

      // Setear id_insumo
      setIdInsumo(record.id_insumo); // Asegúrate que este campo existe en OrdenData

      // Resto de los seteos
      setSelectedRow(record.id ?? null);
      setOrdenCompra(record.orden ?? '');
      setDescripcionInsumo(record.insumo_descripcion ?? '');
      setCantidadIngresada(record.cantidad ?? 0);
      setCantidadFacturada(record.cantidad_facturada ?? 0);
      setPrecioUnitario(record.precio_unitario ?? 0);
      setSelectedTasaIva(record.tasaiva?.toString() ?? '0');
      setImpuestoInterno(record.iinterno ?? 0);

      // console.log('id_insumo seteado:', record.id_insumo); // Debug
    } catch (error) {
      console.error('Error en handleIngresar:', error);
      message.error('Error al procesar el registro seleccionado');
    }
  };

  // Actualizar la definición de las columnas
  const columns: ColumnsType<OrdenData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '5%',
      align: 'right'
    },
    {
      title: 'Orden',
      dataIndex: 'orden',
      width: '15%',
    },
    {
      title: 'Insumo',
      dataIndex: 'insumo_descripcion',
      width: '25%',
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Tasa<br />Iva %
        </div>
      ),
      dataIndex: 'tasaiva',
      width: '8%',
      align: 'right',
      render: (value: number) => {
        const numValue = parseFloat(value.toString());
        return numValue ? numValue.toFixed(2) : '0.00';
      }
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Entregada
        </div>
      ),
      dataIndex: 'cantidad',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Facturada
        </div>
      ),
      dataIndex: 'cantidad_facturada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Unitario',
      dataIndex: 'precio_unitario',
      width: '12%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Subtotal',
      dataIndex: 'subtotal',
      width: '12%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '8%',
      align: 'center',
      render: (_, record) => {
        const estaEnNuevo = datosNuevo.some(item => item.id === record.id);
        return (
          <Button
            type="primary"
            size="small"
            danger={estaEnNuevo}
            disabled={estaEnNuevo}
            onClick={() => handleIngresar(record)}
          >
            Ingresar
          </Button>
        );
      },
    }
  ];

  // Función para rellenar con ceros
  const padWithZeros = (num: string, targetLength: number): string => {
    return num.padStart(targetLength, '0');
  };

  // Función para formatear números con ceros a la izquierda
  //  const padLeftZeros = (num: string, length: number) => {
  //   return num.toString().padStart(length, '0');
  // };

  // Manejadores para los inputs
  const handlePuntoVentaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remover cualquier caracter que no sea número
    const value = e.target.value.replace(/\D/g, '');
    // Limitar a 4 dígitos
    setPuntoVenta(value.slice(0, 4));
  };

  const handlePuntoVentaBlur = () => {
    // Rellenar con ceros cuando pierde el foco
    setPuntoVenta(padWithZeros(puntoVenta, 4));
  };

  const handleNumeroComprobanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remover cualquier caracter que no sea número
    const value = e.target.value.replace(/\D/g, '');
    // Limitar a 8 dígitos
    setNumeroComprobante(value.slice(0, 8));
  };

  const handleNumeroComprobanteBlur = () => {
    // Rellenar con ceros cuando pierde el foco
    setNumeroComprobante(padWithZeros(numeroComprobante, 8));
  };

  // Función para calcular SubLinea
  useEffect(() => {
    const total = impuestoInterno + subtotalSinIva + impuestoIva;
    setSubLinea(total);
  }, [impuestoInterno, subtotalSinIva, impuestoIva]);

  // Actualizar la función recalcularValores
  const recalcularValores = (cantidad: number, precio: number, tasaIva: number, iinterno: number) => {
    // Calcular subtotal bruto (precio * cantidad)
    const subtotalBruto = cantidad * precio;

    // Calcular subtotal sin IVA = (precio * cantidad - iinterno) / (1 + tasaIva/100)
    const subtotalSinIva = Number(((subtotalBruto - iinterno) / (1 + tasaIva / 100)).toFixed(2));
    setSubtotalSinIva(subtotalSinIva);

    // Calcular IVA = (subtotalBruto - iinterno) - subtotalSinIva
    const iva = Number(((subtotalBruto - iinterno) - subtotalSinIva).toFixed(2));
    setImpuestoIva(iva);
    // console.log('1',subtotalBruto,subtotalSinIva,iinterno,iva);
  };

  // Actualizar la definición de las columnas del nuevo grid
  const columnasNuevo: ColumnsType<NuevoGridData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '5%',
      align: 'right'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Detalle<br />Insumos
        </div>
      ),
      dataIndex: 'insumo',
      width: '20%'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Entregada
        </div>
      ),
      dataIndex: 'cantidad_ingresada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: () => (
        <div style={{ lineHeight: '1.2' }}>
          Cantidad<br />Facturada
        </div>
      ),
      dataIndex: 'cantidad_facturada',
      width: '10%',
      align: 'right',
      render: (value: number) => value?.toFixed(2) || '0.00'
    },
    {
      title: 'Unitario',
      dataIndex: 'precio_unitario',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'I.Interno',
      dataIndex: 'impuesto_interno',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Sub s/IVA',
      dataIndex: 'subtotal',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'IVA',
      dataIndex: 'iva',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Total',
      dataIndex: 'total',
      width: '10%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '8%',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          danger
          onClick={() => handleEliminar(record.id)}
        >
          Eliminar
        </Button>
      ),
    }
  ];

  // Manejador para el botón Aceptar
  const handleAceptar = () => {
    if (!idInsumo) {
      message.warning('Debe seleccionar un insumo');
      return;
    }

    // Validaciones de campos requeridos
    const tipoComprobante = form.getFieldValue('tipoComprobante');
    const letraComprobante = form.getFieldValue('letraComprobante');

    if (!tipoComprobante) {
      message.warning('Debe seleccionar un tipo de comprobante');
      return;
    }

    if (!letraComprobante) {
      message.warning('Debe seleccionar una letra de comprobante');
      return;
    }

    if (!puntoVenta) {
      message.warning('Debe ingresar un punto de venta');
      return;
    }

    if (!numeroComprobante) {
      message.warning('Debe ingresar un número de comprobante');
      return;
    }

    // Crear el nuevo registro para el grid
    const nuevoRegistro: NuevoGridData = {
      id: selectedRow ?? 0,
      insumo: descripcionInsumo,
      id_insumo: idInsumo,
      cantidad_ingresada: cantidadIngresada,
      orden: ordenCompra,
      cantidad_facturada: cantidadFacturada,
      precio_unitario: precioUnitario,
      impuesto_interno: impuestoInterno,
      subtotal: subtotalSinIva,
      tasa_iva: parseFloat(selectedTasaIva),
      iva: impuestoIva,
      total: subLinea
    };

    // console.log('Nuevo registro a agregar:', nuevoRegistro); // Debug
    setDatosNuevo([...datosNuevo, nuevoRegistro]);

    // Actualizar totales solo si no es RMT
    if (tipoComprobante !== 'RMT') {
      setTotalImpuestoInterno(prev => Number((prev + impuestoInterno).toFixed(2)));
      setTotalSubtotalSinIva(prev => Number((prev + subtotalSinIva).toFixed(2)));
      setTotalIva(prev => Number((prev + impuestoIva).toFixed(2)));
      setTotalGeneral(prev => Number((prev + (subtotalSinIva + impuestoIva + impuestoInterno)).toFixed(2)));
    }

    // Limpiar los campos de Items
    setSelectedRow(null);
    setOrdenCompra('');
    setDescripcionInsumo('');
    setCantidadIngresada(0);
    setCantidadFacturada(0);
    setPrecioUnitario(0);
    setImpuestoInterno(0);
    setSubtotalSinIva(0);
    setSelectedTasaIva('0');
    setImpuestoIva(0);

    message.success('Ítem agregado correctamente');
  };

  // Agregar manejador para eliminar registro del grid nuevo
  const handleEliminar = (idRegistro: number) => {
    // Encontrar el item a eliminar
    const itemAEliminar = datosNuevo.find(item => item.id === idRegistro);

    if (itemAEliminar) {
      // Actualizar totales restando los valores del item eliminado
      setTotalImpuestoInterno(prev => Number((prev - itemAEliminar.impuesto_interno).toFixed(2)));
      setTotalSubtotalSinIva(prev => Number((prev - itemAEliminar.subtotal).toFixed(2)));
      setTotalIva(prev => Number((prev - itemAEliminar.iva).toFixed(2)));
      setTotalGeneral(prev => Number((prev - itemAEliminar.total).toFixed(2)));
    }

    // Filtrar el registro a eliminar
    const nuevosRegistros = datosNuevo.filter(item => item.id !== idRegistro);
    setDatosNuevo(nuevosRegistros);

    message.success('Ítem eliminado correctamente');
  };

  // Agregamos un useEffect para calcular los totales cuando cambie datosNuevo
  useEffect(() => {
    const totales = datosNuevo.reduce((acc, item) => {
      return {
        impuestoInterno: acc.impuestoInterno + item.impuesto_interno,
        subtotalSinIva: acc.subtotalSinIva + item.subtotal,
        iva: acc.iva + item.iva,
        total: acc.total + item.total
      };
    }, { impuestoInterno: 0, subtotalSinIva: 0, iva: 0, total: 0 });

    setTotalImpuestoInterno(Number(totales.impuestoInterno.toFixed(2)));
    setTotalSubtotalSinIva(Number(totales.subtotalSinIva.toFixed(2)));
    setTotalIva(Number(totales.iva.toFixed(2)));
    setTotalGeneral(Number(totales.total.toFixed(2)));
  }, [datosNuevo]);

  // Función para cargar las percepciones
  const cargarPercepciones = async () => {
    try {
      setLoadingPercepciones(true);
      const token = await getAccessTokenSilently();  // Obtener el token de Auth0    
      //     method: 'GET',
      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/listar_percepciones`;
      const response = await fetch(ruta, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const percepcionesActivas = data.filter((perc: Percepcion) => perc.alta === 1);
      setPercepciones(percepcionesActivas);
      console.log('percepcionesActivas', percepcionesActivas);
    } catch (error) {
      console.error('Error al cargar percepciones:', error);
      message.error('Error al cargar las percepciones');
    } finally {
      setLoadingPercepciones(false);
    }
  };

  // Agregar useEffect para cargar las percepciones al montar el componente
  useEffect(() => {
    cargarPercepciones();
  }, []);

  // Agregar función para manejar el agregar percepción al grid
  const handleAgregarPercepcion = () => {
    if (!selectedPercepcion) {
      message.warning('Debe seleccionar una percepción');
      return;
    }
    if (!valorPercepcion) {
      message.warning('Debe ingresar un valor para la percepción');
      return;
    }

    const percepcionSeleccionada = percepciones.find(p => p.id === Number(selectedPercepcion));
    if (!percepcionSeleccionada) return;

    const nuevaPercepcion: PercepcionGridData = {
      id: percepcionSeleccionada.id,
      percepcion: `${percepcionSeleccionada.provincia} ${percepcionSeleccionada.sigla}`,
      importe: valorPercepcion
    };

    setPercepcionesGrid([...percepcionesGrid, nuevaPercepcion]);

    // Limpiar los campos después de agregar
    setSelectedPercepcion('');
    setValorPercepcion(0);
  };

  // Agregar función para eliminar percepción del grid
  const handleEliminarPercepcion = (id: number) => {
    setPercepcionesGrid(percepcionesGrid.filter(item => item.id !== id));
  };

  // Definir las columnas para el grid de percepciones
  const columnasPercepciones: ColumnsType<PercepcionGridData> = [
    {
      title: 'Id',
      dataIndex: 'id',
      width: '10%',
      align: 'right'
    },
    {
      title: 'Percepción',
      dataIndex: 'percepcion',
      width: '30%'
    },
    {
      title: 'Importe',
      dataIndex: 'importe',
      width: '15%',
      align: 'right',
      render: (value: number) => value ? `$${value.toFixed(2)}` : '$0.00'
    },
    {
      title: 'Acción',
      key: 'action',
      width: '15%',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          danger
          onClick={() => handleEliminarPercepcion(record.id)}
        >
          Eliminar
        </Button>
      )
    }
  ];

  // Agregar un useEffect para calcular el total de percepciones
  useEffect(() => {
    const total = percepcionesGrid.reduce((sum, item) => sum + item.importe, 0);
    setTotalPercepciones(Number(total.toFixed(2)));
  }, [percepcionesGrid]);

  // Función para reiniciar el formulario
  const handleCancelar = () => {
    // Reiniciar estados del formulario
    form.resetFields();
    setSelectedRow(null);
    setOrdenCompra('');
    setDescripcionInsumo('');
    setCantidadIngresada(0);
    setCantidadFacturada(0);
    setPrecioUnitario(0);
    setImpuestoInterno(0);
    setSubtotalSinIva(0);
    setSelectedTasaIva('0');
    setImpuestoIva(0);
    setSubLinea(0);

    // Reiniciar estados de comprobante
    setPuntoVenta('');
    setNumeroComprobante('');

    // Reiniciar datos de grids
    setDatosNuevo([]);
    setPercepcionesGrid([]);

    // Reiniciar totales
    setTotalImpuestoInterno(0);
    setTotalSubtotalSinIva(0);
    setTotalIva(0);
    setTotalGeneral(0);
    setTotalPercepciones(0);

    // Reiniciar percepciones
    setSelectedPercepcion('');
    setValorPercepcion(0);

    // Limpiar órdenes si es necesario
    setOrdenes([]);
    setDatosNuevo([]);
    setPercepcionesGrid([]);
    message.success('Formulario reiniciado');
  };

  // Función para manejar Finalizar Tarea
  const handleFinalizarTarea = async () => {
    try {
      setIsSaving(true);
      const values = await form.validateFields();

      // Validaciones
      if (!values.proveedor) {
        message.error('Debe seleccionar un proveedor');
        return;
      }
      if (datosNuevo.length === 0) {
        message.error('Debe ingresar al menos un ítem');
        return;
      }

      // Crear objeto completo con todos los datos
      const comprobanteCompleto: ComprobanteCompleto = {
        comprobantes: {
          tipo: values.tipoComprobante,
          nro_comprobante: `${values.letraComprobante}${puntoVenta}${numeroComprobante}`,
          padron_id: values.proveedor,
          padron_nombre: padrones.find(p => p.id === values.proveedor)?.razon || '',
          fecha_emision: new Date().toISOString().split('T')[0],
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        },
        movimientos: datosNuevo.map(item => ({
          id: item.id,
          insumo: item.insumo,
          id_insumo: item.id_insumo,
          cantidad: item.cantidad_ingresada,
          cantidad_facturada: item.cantidad_facturada,
          precio_unitario: item.precio_unitario,
          subtotal: item.subtotal,
          tasa_iva: item.tasa_iva,
          iva: item.iva,
          iinterno: item.impuesto_interno,
          tipo_movimiento: 2,
          ordencompra: item.orden,
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        })),
        percepciones: percepcionesGrid.map(perc => ({
          id: perc.id,
          percepcion: perc.percepcion,
          importe: perc.importe,
          id_empresa: user?.id_empresa || 0,
          id_sucursal: user?.id_sucursal || 0
        }))
      };

      // Verificar datos completos
      if (!comprobanteCompleto.comprobantes.id_empresa || !comprobanteCompleto.comprobantes.id_sucursal) {
        message.error('Error: Datos de empresa/sucursal no disponibles');
        return;
      }

      // console.log('Datos a grabar:', comprobanteCompleto);

      const token = await getAccessTokenSilently();

      message.loading({
        content: 'Actualizando comprobante...',
        key: 'saveMessage',
        duration: 0
      });

      const ruta = `${process.env.REACT_APP_INSUMOS_URL}/editar_comprobante_ingreso/${empresa}/${sucursal}/${id}`;
      const response = await fetch(ruta, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(comprobanteCompleto)
      });

      if (!response.ok) {
        throw new Error('Error al grabar el comprobante');
      }

      const resultado = await response.json();
      //console.log('Resultado:', resultado);

      message.success({
        content: 'Comprobante grabado exitosamente',
        key: 'saveMessage',
        duration: 2
      });

      // Redirect to listar_ingresos route
      navigate('/listar_ingresos');

    } catch (error) {
      console.error('Error:', error);
      message.error({
        content: 'Error al grabar el comprobante',
        key: 'saveMessage',
        duration: 2
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Agregar useEffect para el reloj
  useEffect(() => {
    const updateClock = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString();
      setCurrentTime(timeString);
    };

    // Actualizar inmediatamente
    updateClock();

    // Actualizar cada segundo
    const timer = setInterval(updateClock, 1000);

    // Cleanup
    return () => clearInterval(timer);
  }, []);

  // En el useEffect o donde se cargan los datos
  useEffect(() => {
    // console.log('Ordenes:', ordenes);
    // console.log('DatosNuevo:', datosNuevo);
    // console.log('PercepcionesGrid:', percepcionesGrid);
  }, [ordenes, datosNuevo, percepcionesGrid]);

  // Función para cargar insumos
  const fetchInsumos = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/listar_insumos/${user?.id_empresa}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar insumos');
      }

      const data = await response.json();
      setInsumos(data);
      console.log('Insumos:', insumos);
    } catch (error) {
      console.error('Error cargando insumos:', error);
      message.error('Error al cargar la lista de insumos');
    }
  };

  // Cargar insumos cuando el componente se monta
  useEffect(() => {
    if (!isLoadingAuth && user?.id_empresa) {
      fetchInsumos();
    }
  }, [isLoadingAuth, user?.id_empresa]);

  // Función para cargar los datos del comprobante
  const fetchComprobanteData = async () => {
    if (!id || !empresa) return;

    try {
      setIsLoading(true);
      const token = await getAccessTokenSilently();
      const response = await fetch(`${process.env.REACT_APP_INSUMOS_URL}/editar_comprobante_ingreso/${empresa}/${sucursal}/${id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

      if (!response.ok) {
        throw new Error('Error al cargar los datos del comprobante');
      }

      const data = await response.json() as ComprobanteCompleto;
      setComprobanteData(data);

      // Asegurarse que el proveedor esté cargado antes de setear el valor
      //  await fetchPadrones(); // Esperar a que se carguen los padrones

      // Extraer los valores del comprobante
      const tipo = data.comprobantes.tipo;
      const letra = data.comprobantes.nro_comprobante[0];
      const pv = data.comprobantes.nro_comprobante.substring(1, 5);
      const numero = data.comprobantes.nro_comprobante.substring(5);
      const proveedorId = data.comprobantes.padron_id;

      // console.log('Datos del proveedor:', {
      //   proveedorId,
      //   padronesDisponibles: padrones.map(p => ({ id: p.id, razon: p.razon }))
      // });

      // Setear los valores en el formulario
      form.setFieldsValue({
        proveedor: proveedorId,
        tipoComprobante: tipo,
        letraComprobante: letra,
        puntoVenta: pv,
        numeroComprobante: numero
      });

      setPuntoVenta(pv);
      setNumeroComprobante(numero);

      // Cargar las órdenes del proveedor
      if (proveedorId) {
        await fetchOrdenes(proveedorId);
      }

      // Cargar los movimientos en el grid
      const movimientosFormateados = data.movimientos.map(mov => ({
        id: mov.id,
        insumo: mov.insumo,
        id_insumo: mov.id_insumo,
        cantidad_ingresada: mov.cantidad,
        orden: mov.ordencompra,
        cantidad_facturada: mov.cantidad_facturada,
        precio_unitario: mov.precio_unitario,
        impuesto_interno: mov.iinterno,
        subtotal: mov.subtotal,
        tasa_iva: mov.tasa_iva,
        iva: mov.iva,
        total: mov.subtotal + mov.iva + mov.iinterno
      }));
      setDatosNuevo(movimientosFormateados);

      // Cargar las percepciones en el grid con tipos explícitos
      const percepcionesFormateadas = data.percepciones.map((perc: { id: number, percepcion: string, importe: number }) => ({
        id: perc.id,
        percepcion: perc.percepcion,
        importe: perc.importe
      } as PercepcionGridData));  // Aseguramos que el tipo coincida con PercepcionGridData

      setPercepcionesGrid(percepcionesFormateadas);

    } catch (error) {
      console.error('Error cargando comprobante:', error);
      message.error('Error al cargar los datos del comprobante');
    } finally {
      setIsLoading(false);
    }
  };

  // Agregar el manejador para el cambio de insumo
  const handleInsumoChange = (value: number) => {
    //  console.log('Value recibido:', value);
    //  console.log('Ordenes disponibles:', insumos);

    // Buscar en todas las órdenes el insumo con el ID seleccionado
    const insumoSeleccionado = insumos.find(o => o.id === value);

    //console.log('Insumo encontrado:', insumoSeleccionado);

    if (insumoSeleccionado) {
      setIdInsumo(insumoSeleccionado.id);
      setDescripcionInsumo(insumoSeleccionado.descripcion);
      setSelectedTasaIva(insumoSeleccionado.tasaiva.toString());

      // Calcular los valores cuando tengamos cantidadIngresada
      if (cantidadIngresada > 0) {
        const subtotal = precioUnitario * cantidadIngresada;
        const iva = subtotal * (parseFloat(selectedTasaIva) / 100);

        setSubtotalSinIva(subtotal);
        setImpuestoIva(iva);
        setSubLinea(subtotal + iva + impuestoInterno);
      }
    } else {
      console.warn('No se encontró el insumo con ID:', value);
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      {/* Título principal y reloj */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        position: 'relative'  // Para posicionamiento absoluto del reloj
      }}>
        {/* Contenedor del título centrado */}
        <div style={{
          width: '100%',
          textAlign: 'center'  // Centra el texto
        }}>
          <h2>Editar Ingresos</h2>
        </div>

        {/* Reloj posicionado a la derecha */}
        <div style={{
          position: 'absolute',  // Posicionamiento absoluto
          right: 0,             // Alineado a la derecha
          backgroundColor: '#f0f2f5',
          padding: '8px 16px',
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '1.2em'
        }}>
          {currentTime}
        </div>
      </div>

      {/* Formulario de cabecera */}
      <Form form={form} layout="vertical">
        {/* Contenedor de los controles en línea */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '20px',
          marginBottom: '20px'
        }}>
          {/* Select de proveedor */}
          <Form.Item
            name="proveedor"
            rules={[{ required: true, message: 'Por favor seleccione un proveedor' }]}
            style={{ marginBottom: 0 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Proveedor:</span>
              <Select
                showSearch
                placeholder="Seleccione un proveedor"
                value={form.getFieldValue('proveedor')}
                style={{ width: '400px' }}
                optionFilterProp="children"
                onChange={(value) => {
                  handleProveedorChange(value);
                  form.setFieldsValue({ proveedor: value });
                }}
                filterOption={(input, option) =>
                  (option?.children as unknown as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {padrones.map(padron => (
                  <Option key={padron.id} value={padron.id}>
                    {padron.razon}
                  </Option>
                ))}
              </Select>
            </div>
          </Form.Item>

          {/* Select de tipo comprobante */}
          <Form.Item
            name="tipoComprobante"
            rules={[{ required: true, message: 'Por favor seleccione el tipo de comprobante' }]}
          >
            <Select
              placeholder="Seleccione tipo"
              onChange={(value) => {
                if (value === 'FAC') {
                  setCantidadIngresada(0);
                }
                if (value === 'RMT') {
                  setCantidadFacturada(0);
                  setPrecioUnitario(0);
                  setImpuestoInterno(0);
                  setSubtotalSinIva(0);
                  setImpuestoIva(0);
                }
              }}
            >
              <Option value="FAC">FAC - Factura</Option>
              <Option value="F/R">F/R - Factura/Remito</Option>
              <Option value="RMT">RMT - Remito</Option>
              <Option value="NCD">NCD - Nota de Crédito</Option>
            </Select>
          </Form.Item>

          {/* Letra Comprobante */}
          <Form.Item
            name="letraComprobante"
            rules={[{ required: true, message: 'Por favor seleccione la letra' }]}
          >
            <Select placeholder="Letra">
              <Option value="A">A</Option>
              <Option value="B">B</Option>
              <Option value="C">C</Option>
              <Option value="X">X</Option>
            </Select>
          </Form.Item>

          {/* Punto de Venta */}
          <Form.Item
            name="puntoVenta"
            rules={[{ required: true, message: 'Por favor ingrese el punto de venta' }]}
          >
            <Input
              value={puntoVenta}
              onChange={handlePuntoVentaChange}
              onBlur={handlePuntoVentaBlur}
              maxLength={4}
              style={{ width: '80px' }}
            />
          </Form.Item>

          {/* Número de Comprobante */}
          <Form.Item
            name="numeroComprobante"
            rules={[{ required: true, message: 'Por favor ingrese el número de comprobante' }]}
          >
            <Input
              value={numeroComprobante}
              onChange={handleNumeroComprobanteChange}
              onBlur={handleNumeroComprobanteBlur}
              maxLength={8}
              style={{ width: '120px' }}
            />
          </Form.Item>
        </div>
      </Form>

      {/* Grid ORDEN */}
      <div style={{
        marginBottom: '10px',
        textAlign: 'center'  // Centrar el título
      }}>
        <h3>Órdenes Pendientes</h3>
      </div>
      <div style={{
        marginTop: '20px',
        backgroundColor: '#f5f5f5',
        padding: '10px',
        borderRadius: '4px'
      }}>
        <Table
          columns={columns}
          dataSource={ordenes}
          pagination={false}
          scroll={{ y: 180 }}
          size="small"
          style={{ backgroundColor: '#f5f5f5' }}
          rowKey={(record) => {
            const key = record.id.toString();
            //        console.log('Key generada para orden:', key);
            return key;
          }}
        />

      </div>

      {/* Detalle del Item */}
      <div style={{
        padding: '20px',
        border: '2px solid #d9d9d9',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
      }}>
        {/* Todos los campos en una línea */}
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'flex-end'  // Alinea los inputs en la parte inferior
        }}>
          {/* Orden Compra */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ lineHeight: '1.2', whiteSpace: 'normal' }}>
              Orden Compra
            </label>
            <Input
              style={{ width: '90px' }}
              maxLength={8}
              readOnly
              value={ordenCompra}
            />
          </div>

          {/* Descripción Insumo */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ lineHeight: '1.2', whiteSpace: 'normal' }}>
              Descripción Insumo
            </label>
            <Select
              showSearch
              value={idInsumo || undefined}
              onChange={handleInsumoChange}
              style={{ width: '200px' }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {insumos.map(insumo => (
                <Option key={insumo.id} value={insumo.id}>
                  {insumo.descripcion}
                </Option>
              ))}
            </Select>
          </div>

          {/* Cantidad Ingresada/Entregada */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Cantidad Entregada</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              value={form.getFieldValue('tipoComprobante') === 'FAC' ? 0 : cantidadIngresada}
              disabled={form.getFieldValue('tipoComprobante') === 'FAC'}
              onChange={value => setCantidadIngresada(Number(value) || 0)}
            />
          </div>

          {/* Cantidad Facturada */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Cantidad Facturada</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              value={form.getFieldValue('tipoComprobante') === 'RMT' ? 0 : cantidadFacturada}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={value => {
                const cantidad = Number(value) || 0;
                setCantidadFacturada(cantidad);
              }}
              onBlur={() => {
                recalcularValores(cantidadFacturada, precioUnitario, Number(selectedTasaIva), impuestoInterno);
              }}
            />
          </div>

          {/* Precio Unitario */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Precio Unitario</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              prefix="$"
              value={form.getFieldValue('tipoComprobante') === 'RMT' ? 0 : precioUnitario}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={value => {
                const precio = Number(value) || 0;
                setPrecioUnitario(precio);
              }}
              onBlur={() => {
                recalcularValores(cantidadFacturada, precioUnitario, Number(selectedTasaIva), impuestoInterno);
              }}
            />
          </div>

          {/* Impuesto Interno */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Impuesto Interno</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              prefix="$"
              value={form.getFieldValue('tipoComprobante') === 'RMT' ? 0 : impuestoInterno}
              disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
              onChange={value => {
                const nuevoValor = Number(value) || 0;
                setImpuestoInterno(nuevoValor);
              }}
              onBlur={() => {
                recalcularValores(cantidadFacturada, precioUnitario, Number(selectedTasaIva), impuestoInterno);
              }}
            />
          </div>

          {/* Subtotal sin IVA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Subtotal sin IVA</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              prefix="$"
              value={form.getFieldValue('tipoComprobante') === 'RMT' ? 0 : subtotalSinIva}
              disabled={true} // Siempre deshabilitado ya que es calculado
            />
          </div>

          {/* Impuesto IVA */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label>Impuesto IVA</label>
            <InputNumber
              style={{ width: '96px' }}
              precision={2}
              prefix="$"
              value={form.getFieldValue('tipoComprobante') === 'RMT' ? 0 : impuestoIva}
              disabled={true} // Siempre deshabilitado ya que es calculado
            />
          </div>
        </div>
      </div>

      {/* Sección SubLinea y botón Aceptar */}
      <div style={{
        marginTop: '20px',
        padding: '20px',
        border: '2px solid #d9d9d9',
        borderRadius: '4px',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '30%',           // Ancho del 30%
        margin: '20px auto',    // Centra el recuadro horizontalmente
      }}>
        <div style={{
          display: 'flex',
          gap: '20px',
          alignItems: 'flex-end',
          width: '100%',        // Ocupa todo el ancho disponible
          justifyContent: 'center'  // Centra los elementos dentro
        }}>
          {/* SubLinea */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              lineHeight: '1.2',
              whiteSpace: 'normal',
              width: '80px',
              textAlign: 'center'
            }}>
              SubLinea
            </label>
            <div style={{
              fontWeight: 'bold',
              textDecoration: 'underline',
              textAlign: 'right',
              color: 'red'
            }}>
              <InputNumber
                style={{ width: '120px' }}
                disabled
                precision={2}
                prefix="$"
                value={subLinea}
              />
            </div>
          </div>

          {/* Botón Aceptar */}
          <Button
            type="primary"
            style={{ marginBottom: '1px' }}
            onClick={handleAceptar}
            disabled={isLoadingPadron}
          >
            Aceptar
          </Button>
        </div>
      </div>

      {/* Grid NUEVO */}
      <div style={{
        marginBottom: '10px',
        marginTop: '20px',
        textAlign: 'center'  // Centrar el título
      }}>
        <h3>Items Ingresados</h3>
      </div>
      <div style={{
        backgroundColor: '#f5f5f5',
        padding: '10px',
        borderRadius: '4px'
      }}>
        <Table
          columns={columnasNuevo}
          dataSource={datosNuevo}
          pagination={false}
          scroll={{ y: 180 }}
          size="small"
          style={{ backgroundColor: '#f5f5f5' }}
          rowKey={(record) => {
            const key = `nuevo_${record.id}`;
            //      console.log('Key generada para nuevo:', key);
            return key;
          }}
        />
      </div>

      {/* Sección de Totales */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '20px',
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px',
        marginTop: '10px'
      }}>
        {/* Label Totales */}
        <div style={{
          fontWeight: 'bold',
          color: '#000000',
          minWidth: '80px'
        }}>
          Totales:
        </div>

        {/* Contenedor de los valores */}
        <div style={{
          display: 'flex',
          gap: '20px',
          flex: 1
        }}>
          {/* Impuesto Interno */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'flex-end'
          }}>
            <label style={{
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '110px',
              textAlign: 'right'
            }}>
              Impuesto Interno:
            </label>
            <InputNumber
              style={{ width: '150px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalImpuestoInterno}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Subtotal sinIVA */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'flex-end',
            borderTop: '2px solid #d9d9d9',
            paddingTop: '10px'
          }}>
            <label style={{
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '110px',
              textAlign: 'right'
            }}>
              Subtotal sinIVA:
            </label>
            <InputNumber
              style={{ width: '150px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalSubtotalSinIva}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Impuesto IVA */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'flex-end',
            borderTop: '2px solid #d9d9d9',
            paddingTop: '10px'
          }}>
            <label style={{
              fontWeight: 'bold',
              color: '#000000',
              minWidth: '110px',
              textAlign: 'right'
            }}>
              IVA:
            </label>
            <InputNumber
              style={{ width: '110px' }}
              className="right-aligned-input"
              disabled
              precision={2}
              prefix="$"
              value={totalIva}
              formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </div>

          {/* Columna de SubTotal General, Percepciones y Total General */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',  // Alinea los elementos verticalmente
            gap: '10px'
          }}>
            {/* SubTotal General */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'flex-end',
              borderTop: '2px solid #d9d9d9',
              paddingTop: '10px'
            }}>
              <label style={{
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '110px',
                textAlign: 'right'
              }}>
                SubTotal General:
              </label>
              <InputNumber
                style={{ width: '150px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalGeneral}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>

            {/* Percepciones */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'flex-end'
            }}>
              <label style={{
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '110px',
                textAlign: 'right'
              }}>
                Percepciones:
              </label>
              <InputNumber
                style={{ width: '150px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalPercepciones}
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>

            {/* Total General */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              justifyContent: 'flex-end',
              borderTop: '2px solid #d9d9d9',
              paddingTop: '10px'
            }}>
              <label style={{
                fontWeight: 'bold',
                color: '#000000',
                minWidth: '110px',
                textAlign: 'right'
              }}>
                Total General:
              </label>
              <InputNumber
                style={{ width: '150px' }}
                className="right-aligned-input"
                disabled
                precision={2}
                prefix="$"
                value={totalGeneral + totalPercepciones}  // Suma del total general más percepciones
                formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Sección inferior con Percepciones y Totales */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#f5f5f5',
        padding: '15px',
        borderRadius: '4px',
        marginTop: '10px'
      }}>
        {/* Lado izquierdo: Selección de percepciones */}
        <div style={{ width: '60%' }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            marginBottom: '10px'
          }}>
            {/* Sección de Percepciones */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}>
              {/* Select e Input de percepciones */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <label style={{
                  fontWeight: 'bold',
                  color: '#000000',
                  width: '95px'
                }}>
                  Percepciones:
                </label>
                <Select
                  style={{ width: '300px' }}
                  placeholder="Seleccionar Percepciones"
                  loading={loadingPercepciones}
                  value={selectedPercepcion}
                  onChange={(value) => setSelectedPercepcion(value)}
                  showSearch
                  optionFilterProp="children"
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                >
                  {percepciones.map(perc => (
                    <Option key={perc.id} value={perc.id}>
                      {`${perc.provincia} ${perc.sigla}`}
                    </Option>
                  ))}
                </Select>
                <InputNumber
                  style={{ width: '150px' }}
                  placeholder="Ingrese el valor"
                  precision={2}
                  prefix="$"
                  value={valorPercepcion}
                  onChange={(value) => setValorPercepcion(Number(value) || 0)}
                  formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                />
                <Button
                  type="primary"
                  onClick={handleAgregarPercepcion}
                  disabled={form.getFieldValue('tipoComprobante') === 'RMT'}
                >
                  Agregar
                </Button>
              </div>

              {/* Grid de percepciones */}
              <div style={{
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px'
              }}>
                <Table
                  columns={columnasPercepciones}
                  dataSource={percepcionesGrid}
                  pagination={false}
                  size="small"
                  style={{ backgroundColor: '#f5f5f5' }}
                  rowKey={(record) => {
                    const key = `perc_${record.id}`;
                    //           console.log('Key generada para percepcion:', key);
                    return key;
                  }}
                />
              </div>

              {/* Botones de acción */}
              <div style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',  // Alinea los botones a la derecha
                marginTop: '10px'  // Espacio adicional después del grid
              }}>
                <Button
                  type="primary"
                  onClick={handleFinalizarTarea}
                  disabled={isLoadingPadron || isLoading || datosNuevo.length === 0 || isSaving}
                  loading={isSaving}
                >
                  {isSaving ? 'Grabando...' : 'Actualizar Datos'}
                </Button>
                <Button
                  onClick={handleCancelar}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Lado derecho: Totales (código existente) */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: '40%'
        }}>
          {/* ... resto del código de totales ... */}
        </div>
      </div>
    </div>
  );
};

export default EditarIngresos;
